import { Button, TextField } from '@mui/material'
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material'
import { useState } from 'react'
import { useAddCourse } from './hooks/useAddCourse/useAddCourse'
import { useSnackbar } from 'notistack'
import { ErrorMessageCustomCourseCode, ErrorMessageCustomCourseDepartment } from '../../components/snackbar'

const ButtonNameRemoveCourse = 'Remove Course'
const ButtonNameAddFullLectures = 'Add Full and Blocked Lectures'

interface CustomCourseOption {
    key: string
    label: string
    department: string
    courseNumber: string
    courseDescription: string
    credit: number
    offeredTerms: string[]
}

const blankCustomCourseOption: CustomCourseOption = {
    key: '',
    label: '',
    department: '',
    courseNumber: '',
    courseDescription: '',
    credit: 3,
    offeredTerms: ['1', '2'],
}

interface DialogCustomCourseProps {
    isOpen: boolean
    closeDialog: () => void
    setClearInputBox: React.Dispatch<React.SetStateAction<number>>
    term: string
    session: string
}

export const DialogCustomCourse = ({
    isOpen,
    closeDialog,
    setClearInputBox,
    term,
    session,
}: DialogCustomCourseProps) => {
    const { enqueueSnackbar } = useSnackbar()
    const [customCourse, setCustomCourse] = useState<CustomCourseOption>(blankCustomCourseOption)
    const addCourse = useAddCourse({ setClearInputBox })

    const handleChangeCourseDepartment = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomCourse((customCourse) => ({
            ...customCourse,
            department: event.target.value,
        }))
    }

    const handleChangeCourseNumber = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCustomCourse((customCourse) => ({
            ...customCourse,
            courseNumber: event.target.value,
        }))
    }

    const handleAddCustomCourse = async () => {
        const department = customCourse.department.trim() // e.g., "ECON"
        const courseCode = customCourse.courseNumber.trim() // e.g., "1234" or "1234A"

        // Validation: Department must be 4–5 letters, no numbers
        const isValidDepartment = /^[A-Za-z]{2,4}$/.test(department)

        // Validation: Course code must be exactly 4 digits
        const isValidCourseCode = /^\d{3}[A-Za-z]?$/.test(courseCode)

        if (!isValidDepartment) {
            enqueueSnackbar(ErrorMessageCustomCourseDepartment, { variant: 'error' })
            return
        }

        if (!isValidCourseCode) {
            enqueueSnackbar(ErrorMessageCustomCourseCode, { variant: 'error' })
            return
        }
        const newCustomCourse: CustomCourseOption = {
            ...customCourse,
            department: department.toUpperCase(),
            key: customCourse.department + ' ' + customCourse.courseNumber,
            label: customCourse.department + ' ' + customCourse.courseNumber,
        }

        addCourse(newCustomCourse, term, session)
        closeDialog()
    }

    return (
        <Dialog open={isOpen} onClose={closeDialog}>
            <DialogTitle>Can't Find a Course?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    If a course isn't showing up, it might be because UBC Scheduler hasn't updated with the newest
                    courses yet.
                    <br />
                    <br />
                    You can still add a course by entering the department and course number (like "CPSC 110").
                    <br />
                    <br />
                    <b>Please make sure the course exists in the official UBC course system before adding it.</b>
                </DialogContentText>
                <br />
                <TextField
                    fullWidth
                    label="Course Department"
                    placeholder="Eg. ECON"
                    value={customCourse.department}
                    onChange={handleChangeCourseDepartment}
                />
                <br />
                <br />
                <TextField
                    fullWidth
                    label="Course Number"
                    placeholder="Eg. 101"
                    value={customCourse.courseNumber}
                    onChange={handleChangeCourseNumber}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={closeDialog}>Cancel</Button>
                <Button onClick={handleAddCustomCourse} variant="contained">
                    Add Course
                </Button>
            </DialogActions>
        </Dialog>
    )
}
